/**
 * AdminDashboard.js
 * Admin Portal dashboard with platform statistics and recent activity
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Modal, Card } from '../components/SharedComponents';
import { API_BASE } from '../config';
import colors from '../utils/adminColors';

function AdminDashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingSuppliers, setPendingSuppliers] = useState([]);
  const [approvingId, setApprovingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [downloadingChatExport, setDownloadingChatExport] = useState(false);
  const [chatExportFrom, setChatExportFrom] = useState('');
  const [chatExportTo, setChatExportTo] = useState('');

  useEffect(() => {
    fetchDashboardData();
    fetchPendingSuppliers();
  }, [token]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/dashboard/stats`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        setRecentActivity(data.recent_activity || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSuppliers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/supplier-users?status=pending_approval`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingSuppliers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching pending suppliers:', error);
    }
  };

  const handleApproveSupplier = async (userId, userName) => {
    setApprovingId(userId);
    try {
      const response = await fetch(`${API_BASE}/api/admin/supplier-users/${userId}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: `${userName} has been approved and can now log in.` });
        setPendingSuppliers(prev => prev.filter(s => s.supplier_user_id !== userId));
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to approve user' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to approve user' });
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectSupplier = async (userId, userName) => {
    if (!window.confirm(`Reject ${userName}'s registration? This will prevent them from logging in.`)) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/supplier-users/${userId}/status?status=inactive`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: `${userName}'s registration has been rejected.` });
        setPendingSuppliers(prev => prev.filter(s => s.supplier_user_id !== userId));
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reject user' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 hour
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return `${mins} min${mins !== 1 ? 's' : ''} ago`;
    }
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }
    // Otherwise show date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleResetTestData = async () => {
    setResetting(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/reset-test-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: `Reset complete. Acme's three test suppliers are in place. ${data.steps?.slice(-1)?.[0] || ''}` });
        fetchDashboardData();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Reset failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error connecting to server' });
    } finally {
      setResetting(false);
      setResetConfirm(false);
    }
  };

  // ─── Chat capture CSV export ───
  // Fetches GET /api/admin/chat/capture/export.csv (shipped in commit baa3f67)
  // with the admin Bearer token, converts the streamed text/csv response into a
  // Blob, and triggers a browser download. Mirrors the existing PDF/XLSX
  // download pattern in RFPReviewEditable.js (response.blob() → createObjectURL
  // → anchor.click() → revokeObjectURL).
  //
  // Token-null guard prevents a Bearer-null request during the AuthContext
  // hydration race on cold reload. Error UX uses alert() to match the local
  // download convention.
  // Returns inline error string if range is invalid, otherwise null.
  const chatExportRangeError = (chatExportFrom && chatExportTo && chatExportFrom > chatExportTo)
    ? 'From date must be on or before To date.'
    : null;

  const handleDownloadChatExport = async () => {
    if (!token) return;
    if (chatExportRangeError) return;
    setDownloadingChatExport(true);
    try {
      const params = new URLSearchParams();
      if (chatExportFrom) params.append('from', chatExportFrom);
      if (chatExportTo) params.append('to', chatExportTo);
      const qs = params.toString();
      const requestUrl = `${API_BASE}/api/admin/chat/capture/export.csv${qs ? `?${qs}` : ''}`;
      const response = await fetch(requestUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        let detail = '';
        try { detail = (await response.json())?.detail || ''; } catch (_) { /* non-JSON body */ }
        alert(`Download failed: HTTP ${response.status}${detail ? `: ${detail}` : ''}`);
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // YYYY-MM-DD via toISOString — matches the server's Content-Disposition
      // filename (which uses YYYYMMDD without dashes); both are fine, this
      // hyphenated form is just slightly more human-readable on disk.
      a.download = `chat-capture-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert(`Download failed: ${error?.message || String(error)}`);
    } finally {
      setDownloadingChatExport(false);
    }
  };

  const getActionIcon = (action) => {
    const lockIcon = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
    const starIcon = <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
    const keyIcon = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>;
    const icons = {
      'login': lockIcon,
      'create_company': '+',
      'update_company': '·',
      'delete_company': '×',
      'create_company_user': '+',
      'update_company_user': '·',
      'delete_company_user': '×',
      'create_admin_user': starIcon,
      'password_change': keyIcon,
      'migrate_data': '↑',
      'register': '·',
      'approve_supplier_user': <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    };
    return icons[action] || '·';
  };

  const getActionLabel = (action) => {
    const labels = {
      'login': 'Logged in',
      'create_company': 'Created company',
      'update_company': 'Updated company',
      'delete_company': 'Deleted company',
      'create_company_user': 'Created user',
      'update_company_user': 'Updated user',
      'delete_company_user': 'Deactivated user',
      'create_admin_user': 'Created admin',
      'password_change': 'Changed password',
      'migrate_data': 'Migrated data',
      'register': 'Registered',
      'approve_supplier_user': 'Approved supplier',
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '32px', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: colors.lightGray,
      }}>
        <div style={{ textAlign: 'center' }}>
          
          <div style={{ color: colors.textMuted }}>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ 
          fontSize: 'var(--font-size-hero)',
          fontWeight: 'var(--font-weight-bold)',
          color: colors.darkText,
          marginBottom: '8px',
        }}>
          Welcome back, {user?.full_name?.split(' ')[0] || 'Admin'} 
        </h1>
        <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)' }}>
          Here's what's happening with your platform today.
        </p>
      </div>

      {/* Message Toast */}
      {message && (
        <div style={{
          marginBottom: '24px',
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? `${colors.success}15` : `${colors.danger}15`,
          borderLeft: `4px solid ${message.type === 'success' ? colors.success : colors.danger}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ color: message.type === 'success' ? colors.success : colors.danger }}>
            {message.text}
          </span>
          <button
            onClick={() => setMessage(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-lg)', color: colors.textMuted }}
          >
            ×
          </button>
        </div>
      )}

      {/* Pending Supplier Approvals Alert */}
      {pendingSuppliers.length > 0 && (
        <div style={{
          marginBottom: '24px',
          backgroundColor: colors.white,
          borderRadius: '12px',
          boxShadow: 'var(--shadow-sm)',
          border: `2px solid ${colors.warning}`,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px',
            backgroundColor: `${colors.warning}10`,
            borderBottom: `1px solid ${colors.warning}30`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <span style={{ fontSize: 'var(--font-size-xl)' }}></span>
            <div>
              <div style={{ fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                {pendingSuppliers.length} Supplier Registration{pendingSuppliers.length > 1 ? 's' : ''} Pending Approval
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                Review and approve supplier users to allow them access
              </div>
            </div>
          </div>
          
          <div style={{ padding: '16px 20px' }}>
            {pendingSuppliers.map((supplier, idx) => (
              <div
                key={supplier.supplier_user_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: idx < pendingSuppliers.length - 1 ? `1px solid ${colors.mediumGray}` : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: `${colors.supplierGreen}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: colors.supplierGreen,
                  }}>
                    {supplier.full_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                      {supplier.full_name}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                      {supplier.email} • {supplier.supplier_name}
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleRejectSupplier(supplier.supplier_user_id, supplier.full_name)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      border: `1px solid ${colors.danger}`,
                      borderRadius: '6px',
                      color: colors.danger,
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      cursor: 'pointer',
                    }}
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApproveSupplier(supplier.supplier_user_id, supplier.full_name)}
                    disabled={approvingId === supplier.supplier_user_id}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: colors.supplierGreen,
                      border: 'none',
                      borderRadius: '6px',
                      color: colors.white,
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      cursor: approvingId === supplier.supplier_user_id ? 'not-allowed' : 'pointer',
                      opacity: approvingId === supplier.supplier_user_id ? 0.7 : 1,
                    }}
                  >
                    {approvingId === supplier.supplier_user_id ? 'Approving...' : 'Approve'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '16px', 
        marginBottom: '32px' 
      }}>
        <Card className="card-hover" onClick={() => navigate('/admin/companies')} padding="16px 20px">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText }}>{stats?.companies?.total_companies || 0}</span>
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.success }}>{stats?.companies?.active_companies || 0} active</span>
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>companies</div>
        </Card>
        <Card padding="16px 20px">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText }}>{stats?.users?.total_users || 0}</span>
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.success }}>{stats?.users?.active_users || 0} active</span>
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>company users</div>
        </Card>
        <Card className="card-hover" onClick={() => navigate('/admin/users')} padding="16px 20px">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText }}>{stats?.admins?.total_admins || 0}</span>
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.success }}>{stats?.admins?.active_admins || 0} active</span>
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>admin users</div>
        </Card>
        <Card padding="16px 20px">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText }}>{stats?.users?.active_7d || 0}</span>
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>active users (7 days)</div>
        </Card>
      </div>

      {/* Quick Actions + Recent Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Quick Actions */}
        <Card padding="24px">
          <h2 style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 'var(--font-weight-semibold)',
            color: colors.darkText,
            marginBottom: '24px',
          }}>
            Quick Actions
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <ActionButton
              icon="+"
              label="Create New Company"
              description="Add a new tenant to the platform"
              onClick={() => navigate('/admin/companies/new')}
              color={colors.accent}
            />
            <ActionButton
              icon="+"
              label="Add Company User"
              description="Create a user for an existing company"
              onClick={() => navigate('/admin/companies')}
              color={colors.success}
            />
            <ActionButton
              icon="→"
              label="View Audit Log"
              description="Review recent platform activity"
              onClick={() => navigate('/admin/audit-log')}
              color={colors.adminPurple}
            />
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              padding: '12px 14px',
              border: `1px solid ${colors.borderLight || '#E2E5EA'}`,
              borderRadius: '8px',
              background: '#FAFBFC',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
              }}>
                <label style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  From
                  <input
                    type="date"
                    value={chatExportFrom}
                    onChange={(e) => setChatExportFrom(e.target.value)}
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #E2E5EA',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#111827',
                      background: '#FFFFFF',
                    }}
                  />
                </label>
                <label style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  To
                  <input
                    type="date"
                    value={chatExportTo}
                    onChange={(e) => setChatExportTo(e.target.value)}
                    style={{
                      padding: '6px 8px',
                      border: '1px solid #E2E5EA',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#111827',
                      background: '#FFFFFF',
                    }}
                  />
                </label>
                {(chatExportFrom || chatExportTo) && (
                  <button
                    type="button"
                    onClick={() => { setChatExportFrom(''); setChatExportTo(''); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.accent,
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: '4px 6px',
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
              {chatExportRangeError && (
                <div style={{
                  fontSize: '12px',
                  color: '#DC2626',
                  marginTop: '2px',
                }}>
                  {chatExportRangeError}
                </div>
              )}
            </div>
            <ActionButton
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>}
              label={downloadingChatExport ? 'Downloading…' : 'Download Chat Capture Export'}
              description="Download all captured AI chat turns as CSV"
              onClick={handleDownloadChatExport}
              color={colors.accent}
              disabled={!token || downloadingChatExport}
            />
            <ActionButton
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>}
              label="Reset Test Data"
              description="Clear all RFPs & recreate 3 test supplier accounts"
              onClick={() => setResetConfirm(true)}
              color={colors.danger}
            />
          </div>
        </Card>

        {/* Recent Activity */}
        <Card padding="24px">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}>
            <h2 style={{ 
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              color: colors.darkText,
            }}>
              Recent Activity
            </h2>
            <button
              onClick={() => navigate('/admin/audit-log')}
              style={{
                background: 'none',
                border: 'none',
                color: colors.accent,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
              }}
            >
              View all →
            </button>
          </div>
          
          {recentActivity.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: colors.textMuted,
            }}>
              
              <div>No recent activity</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {recentActivity.slice(0, 6).map((activity, idx) => (
                <div 
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    paddingBottom: idx < recentActivity.length - 1 ? '16px' : 0,
                    borderBottom: idx < recentActivity.length - 1 ? `1px solid ${colors.mediumGray}` : 'none',
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: `${colors.accent}10`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'var(--font-size-base)',
                    flexShrink: 0,
                  }}>
                    {getActionIcon(activity.action)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontSize: 'var(--font-size-base)',
                      color: colors.darkText,
                      fontWeight: 'var(--font-weight-medium)',
                    }}>
                      {getActionLabel(activity.action)}
                    </div>
                    <div style={{ 
                      fontSize: 'var(--font-size-sm)',
                      color: colors.textMuted,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {activity.user_email}
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: 'var(--font-size-sm)',
                    color: colors.textMuted,
                    whiteSpace: 'nowrap',
                  }}>
                    {formatDate(activity.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* New Companies (if any in last 30 days) */}
      {stats?.companies?.new_companies_30d > 0 && (
        <div style={{
          marginTop: '24px',
          backgroundColor: `${colors.success}10`,
          borderRadius: '12px',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}>
          <div style={{ fontSize: 'var(--font-size-hero)', display: 'flex', alignItems: 'center' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></div>
          <div>
            <div style={{ fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
              {stats.companies.new_companies_30d} new {stats.companies.new_companies_30d === 1 ? 'company' : 'companies'} this month!
            </div>
            <div style={{ fontSize: 'var(--font-size-base)', color: colors.textMuted }}>
              Your platform is growing
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <Modal isOpen={!!resetConfirm} onClose={() => setResetConfirm(false)} title="Reset All Test Data?" size="small">
            <p style={{ margin: '0 0 24px', fontSize: 'var(--font-size-base)', color: colors.darkText, lineHeight: '1.6' }}>
              This makes sure Acme's three test suppliers (TitanEdge, AeroCircuit, NovaCast) exist and are linked to Acme.
            </p>
            <p style={{ margin: '0 0 24px', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
              Core data (materials, purchase orders, contracts) will NOT be affected. The old RFP and supplier portal data this used to clear no longer exists.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setResetConfirm(false)}
                disabled={resetting}
                style={{
                  padding: '8px 16px', borderRadius: '8px', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)',
                  border: `1px solid ${colors.mediumGray}`, backgroundColor: colors.white,
                  color: colors.darkText, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleResetTestData}
                disabled={resetting}
                style={{
                  padding: '8px 16px', borderRadius: '8px', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)',
                  border: 'none', backgroundColor: colors.danger,
                  color: colors.white, cursor: 'pointer',
                  opacity: resetting ? 0.6 : 1,
                }}
              >
                {resetting ? 'Resetting...' : 'Yes, Reset Everything'}
              </button>
            </div>
      </Modal>
    </div>
  );
}

function StatCard({ icon, label, value, subValue, color, onClick }) {
  return (
    <Card
      className={onClick ? 'card-hover' : ''}
      onClick={onClick}
      padding="24px"
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '16px',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          backgroundColor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--font-size-hero)',
        }}>
          {icon}
        </div>
        <span style={{ fontSize: 'var(--font-size-base)', color: colors.textMuted, fontWeight: 'var(--font-weight-medium)' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText }}>
        {value}
      </div>
      {subValue && (
        <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginTop: '4px' }}>
          {subValue}
        </div>
      )}
    </Card>
  );
}

function ActionButton({ icon, label, description, onClick, color, disabled = false }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px',
        backgroundColor: colors.lightGray,
        border: 'none',
        borderRadius: '10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        transition: 'background-color 0.2s',
        width: '100%',
        opacity: disabled ? 0.55 : 1,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = `${color}10`; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = colors.lightGray; }}
    >
      <div style={{
        width: '44px',
        height: '44px',
        borderRadius: '10px',
        backgroundColor: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 'var(--font-size-hero)',
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, fontSize: 'var(--font-size-base)' }}>
          {label}
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
          {description}
        </div>
      </div>
    </button>
  );
}

export default AdminDashboard;
